import { ref } from "vue";
import { useUserStore } from "@/stores/user";
import { useRouter } from "vue-router";
import { SignInSchema, MfaVerificationSchema } from "../validations/schemas";

export function useSignIn() {
  const userStore = useUserStore();
  const { mfaVerificationRequired } = userStore;
  const formData = ref({});
  const errors = ref({});
  const isSubmitting = ref(false);
  const submitError = ref(null);
  const mfaRequired = ref(mfaVerificationRequired);
  const mfaCode = ref(null);
  const router = useRouter();

  if (mfaVerificationRequired) {
    userStore.requestMfaToken();
  }

  function redirectToReturnPathOrDashboard() {
    const returnPath = sessionStorage.getItem("returnTo");
    const redirectDestination = returnPath ? { path: returnPath } : { name: "Dashboard" };
    sessionStorage.removeItem("returnTo");
    router.push(redirectDestination);
  }

  function handleErrors(error, genericErrorMessage = "An unexpected error occurred") {
    // Check if it's a validation error (from Zod)
    if (error.errors && Array.isArray(error.errors)) {
      errors.value = error.errors.reduce((acc, err) => {
        acc[err.path[0]] = err.message;
        return acc;
      }, {});
    } else {
      // Handle unexpected errors
      submitError.value = error.message || genericErrorMessage;
    }
  }

  async function handleSignInSubmit() {
    errors.value = {};
    submitError.value = null;
    isSubmitting.value = true;
    try {
      SignInSchema.parse(formData.value);
      const res = await userStore.signIn(formData.value.email, formData.value.password);

      if (!res.success) {
        submitError.value = res.error;
        return;
      }

      if (res.mfaEnrollmentAvailable) {
        router.push({ name: "MfaEnrollment" });
        return;
      } else if (res.mfaRequired) {
        mfaRequired.value = true;
        return;
      }

      redirectToReturnPathOrDashboard();
    } catch (error) {
      handleErrors(error);
    } finally {
      isSubmitting.value = false;
    }
  }

  async function handleMfaSubmit() {
    errors.value = {};
    submitError.value = null;
    isSubmitting.value = true;

    try {
      const validation = MfaVerificationSchema.safeParse(mfaCode.value);
      if (!validation.success) {
        errors.value = { mfaVerificationCode: validation.error.issues[0].message };
        return;
      }
      const res = await userStore.verifyMfaToken(mfaCode.value);
      if (res.success) {
        redirectToReturnPathOrDashboard();
      } else {
        submitError.value = res.error;
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      isSubmitting.value = false;
    }
  }

  async function resendCode() {
    await userStore.requestMfaToken();
  }

  return {
    errors,
    formData,
    handleSignInSubmit,
    handleMfaSubmit,
    mfaRequired,
    mfaCode,
    submitError,
    isSubmitting,
    resendCode,
  };
}
